import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { ApiError } from "../api/client";
import { linkTo } from "../api/links";
import {
  useAcks,
  useBatches,
  useChanges,
  useFeed,
  useLoad,
  useOutbound,
  useVehicles,
} from "../api/queries";
import type { Batch, Change, Load, Vehicle } from "../api/types";
import { AckTable } from "../components/AckTable";
import { BatchTable } from "../components/BatchTable";
import { Columns, ColumnsPanel } from "../components/Columns";
import { FeedTable } from "../components/FeedTable";
import { LoadMore } from "../components/MasterDetail";
import {
  Badge,
  Empty,
  ErrorBox,
  EventType,
  Field,
  FieldGrid,
  Guid,
  Num,
  OutcomeBadge,
  Panel,
  ProvenanceBadge,
  ReasonBadge,
  SEQ,
  Spinner,
  StatusBadge,
  Time,
  shorten,
  useJsonDisclosure,
} from "../components/primitives";
import { OutboundRow } from "./OutboundPage";

export function LoadDetail() {
  const { loadGuid = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "timeline" ? "timeline" : "overview";
  const query = useLoad(loadGuid);
  const batches = useBatches({ root_guid: loadGuid, limit: 200 });
  const missing = query.error instanceof ApiError && query.error.isNotFound;

  return (
    <div className="space-y-4 p-5">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <h1 className="mono text-base text-strong">{query.data?.number ?? shorten(loadGuid)}</h1>
        <Guid value={loadGuid} />
        <nav className="ml-auto flex items-center gap-5">
          {(["overview", "timeline"] as const).map((name) => (
            <button
              key={name}
              type="button"
              className={`tab ${tab === name ? "tab-active" : ""}`}
              onClick={() => {
                const next = new URLSearchParams(params);
                if (name === "overview") next.delete("tab");
                else next.set("tab", name);
                setParams(next, { replace: true });
              }}
            >
              {name === "overview" ? "Overview" : "Timeline"}
            </button>
          ))}
        </nav>
      </div>

      {query.isPending && <Spinner />}
      {query.error && !missing && <ErrorBox error={query.error} />}
      {missing && (
        <div className="rounded-md border border-warn/40 bg-warn-bg p-4 text-sm text-warn">
          No row in the load table — nothing was ever applied for this root. What the journal and
          the inbox know about it is below.
        </div>
      )}

      {tab === "overview" ? (
        <Overview loadGuid={loadGuid} load={query.data} batches={batches} />
      ) : (
        <Timeline loadGuid={loadGuid} batches={batches} />
      )}
    </div>
  );
}

type BatchesQuery = ReturnType<typeof useBatches>;

function Overview({
  loadGuid,
  load,
  batches,
}: {
  loadGuid: string;
  load: Load | undefined;
  batches: BatchesQuery;
}) {
  return (
    <div className="space-y-4">
      {load && <State load={load} />}
      <Vehicles loadGuid={loadGuid} />

      <LoadFeed loadGuid={loadGuid} load={load} />
      <LoadAcks loadGuid={loadGuid} />

      <LoadBatches loadGuid={loadGuid} query={batches} />
      <LoadOutbound loadGuid={loadGuid} />
      {load && <ColumnsPanel table="load" id="load-columns" row={load} self="guid" />}
    </div>
  );
}

function State({ load }: { load: Load }) {
  return (
    <Panel
      title="State"
      actions={
        <div className="flex items-center gap-2">
          {load.deleted && <Badge tone="bad">deleted</Badge>}
          {load.archived && <Badge tone="warn">archived</Badge>}
          <Badge tone="info">v{load.server_version}</Badge>
        </div>
      }
    >
      <FieldGrid>
        <Field label="number">{load.number ?? "—"}</Field>
        <Field label="status">{load.status || "—"}</Field>
        <Field label="inspection">{load.inspection_type}</Field>
        <Field label="leg">{load.current_leg}</Field>
        <Field label="driver">
          <Guid value={load.driver_guid} to={linkTo.driver(load.driver_guid)} />
        </Field>
        <Field label="carrier">
          <Guid value={load.carrier_guid} to={linkTo.carrier(load.carrier_guid)} />
        </Field>
        <Field label="last writer">
          {load.provenance ? <ProvenanceBadge provenance={load.provenance} /> : "—"}
        </Field>
        <Field label="last event">
          <Guid value={load.last_event_guid} to={linkTo.event(load.last_event_guid)} />
        </Field>
        <Field label="ctms id">{load.ctms_id ?? "—"}</Field>
        <Field label="client uuid">
          <Guid value={load.client_uuid} />
        </Field>
        <Field label="created">
          <Time value={load.created_at} relative={false} />
        </Field>
        <Field label="updated">
          <Time value={load.updated_at} relative={false} />
        </Field>
      </FieldGrid>
    </Panel>
  );
}

function Vehicles({ loadGuid }: { loadGuid: string }) {
  const query = useVehicles({ load_guid: loadGuid });
  const vehicles = query.data?.pages.flatMap((page) => page.vehicles) ?? [];

  return (
    <Panel
      id="load-vehicles"
      title={`Vehicles (${vehicles.length}${query.hasNextPage ? "+" : ""})`}
    >
      {query.isPending && <Spinner />}
      {query.error && <ErrorBox error={query.error} />}
      {query.isSuccess && vehicles.length === 0 && <Empty>No vehicle row for this load.</Empty>}
      {vehicles.map((vehicle) => (
        <VehicleRow key={vehicle.guid} vehicle={vehicle} />
      ))}
      <LoadMore
        hasNext={query.hasNextPage}
        loading={query.isFetchingNextPage}
        onClick={() => void query.fetchNextPage()}
      />
    </Panel>
  );
}

function VehicleRow({ vehicle }: { vehicle: Vehicle }) {
  const [open, setOpen] = useState(false);
  const name = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" ");

  return (
    <div className="border-b border-line/70 last:border-b-0">
      <div className="row-hover flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2">
        <button type="button" className="label hover:text-strong" onClick={() => setOpen(!open)}>
          {open ? "▾" : "▸"} row
        </button>
        <span className="mono text-xs text-strong">{vehicle.vin ?? "no vin"}</span>
        {name && <span className="text-xs text-body">{name}</span>}
        {vehicle.deleted && <Badge tone="bad">deleted</Badge>}
        <Guid value={vehicle.guid} />
        <span className="mono text-xs text-muted" title={SEQ.serverVersion}>
          v{vehicle.server_version}
        </span>
        <span className="ml-auto">
          <Time value={vehicle.updated_at} />
        </span>
      </div>
      {open && (
        <div className="border-t border-line/70 bg-canvas">
          <Columns row={vehicle} self="guid" />
        </div>
      )}
    </div>
  );
}

/** Every driver's feed row for this load, so a reassignment shows the old driver's delete. */
function LoadFeed({ loadGuid, load }: { loadGuid: string; load: Load | undefined }) {
  const query = useFeed({ root_guid: loadGuid });
  const feed = query.data?.pages.flatMap((page) => page.feed) ?? [];
  const assigned = Boolean(load?.driver_guid && load.carrier_guid);
  const inCurrentFeed = feed.some(
    (entry) => entry.driver_guid === load?.driver_guid && entry.carrier_guid === load?.carrier_guid,
  );

  return (
    <Panel id="load-feed" title={`Device feed (${feed.length}${query.hasNextPage ? "+" : ""})`}>
      {query.isPending && <Spinner />}
      {query.error && <ErrorBox error={query.error} />}
      {query.isSuccess && feed.length > 0 && assigned && !inCurrentFeed && !query.hasNextPage && (
        <div className="border-b border-line bg-warn-bg px-4 py-2 text-xs text-warn">
          Not in the assigned driver's feed index — their device pull will never see this load.
        </div>
      )}
      {query.isSuccess && (
        <FeedTable
          feed={feed}
          omit={["load"]}
          current={
            load ? { driver_guid: load.driver_guid, carrier_guid: load.carrier_guid } : undefined
          }
          empty="No driver's feed index has this load — no device pull has ever seen it."
        />
      )}
      <LoadMore
        hasNext={query.hasNextPage}
        loading={query.isFetchingNextPage}
        onClick={() => void query.fetchNextPage()}
      />
    </Panel>
  );
}

function LoadAcks({ loadGuid }: { loadGuid: string }) {
  const query = useAcks({ root_guid: loadGuid });
  const acks = query.data?.pages.flatMap((page) => page.acks) ?? [];

  return (
    <Panel title="Applied on device" id="load-acks">
      {query.isPending && <Spinner />}
      {query.error && <ErrorBox error={query.error} />}
      {query.isSuccess && (
        <AckTable acks={acks} omit={["load"]} empty="No device has acked a batch for this load." />
      )}
      <LoadMore
        hasNext={query.hasNextPage}
        loading={query.isFetchingNextPage}
        onClick={() => void query.fetchNextPage()}
      />
    </Panel>
  );
}

function LoadBatches({ loadGuid, query }: { loadGuid: string; query: BatchesQuery }) {
  const batches = query.data?.pages[0]?.batches.slice(0, 20) ?? [];

  return (
    <Panel
      id="load-batches"
      title="Recent batches"
      actions={
        <Link to={linkTo.rootBatches(loadGuid)} className="label hover:text-strong">
          all batches →
        </Link>
      }
    >
      {query.isPending && <Spinner />}
      {query.error && <ErrorBox error={query.error} />}
      {query.isSuccess && (
        <BatchTable
          batches={batches}
          omit={["root"]}
          empty="No batch ever arrived for this load."
        />
      )}
    </Panel>
  );
}

function LoadOutbound({ loadGuid }: { loadGuid: string }) {
  const query = useOutbound({ root_guid: loadGuid });
  const messages = query.data?.pages.flatMap((page) => page.outbound) ?? [];

  return (
    <Panel
      id="load-outbound"
      title={`Outbound (${messages.length}${query.hasNextPage ? "+" : ""})`}
    >
      {query.isPending && <Spinner />}
      {query.error && <ErrorBox error={query.error} />}
      {query.isSuccess && messages.length === 0 && (
        <Empty>Nothing staged for CTMS — mobile-only events stage no outbound message.</Empty>
      )}
      {messages.map((message) => (
        <OutboundRow key={message.id} message={message} />
      ))}
      <LoadMore
        hasNext={query.hasNextPage}
        loading={query.isFetchingNextPage}
        onClick={() => void query.fetchNextPage()}
      />
    </Panel>
  );
}

/** The journal, newest first, grouped under the batch that carried each event. */
function Timeline({ loadGuid, batches: inbox }: { loadGuid: string; batches: BatchesQuery }) {
  const query = useChanges({ root_guid: loadGuid, limit: 100 });
  const changes = query.data?.pages.flatMap((page) => page.changes) ?? [];
  const batches = new Map(
    (inbox.data?.pages.flatMap((page) => page.batches) ?? []).map((batch) => [
      batch.batch_guid,
      batch,
    ]),
  );

  if (query.isPending) return <Spinner />;
  if (query.error) return <ErrorBox error={query.error} />;

  const groups = groupByBatch(changes);

  return (
    <Panel id="load-journal" title={`Journal (${changes.length}${query.hasNextPage ? "+" : ""})`}>
      {groups.length === 0 && (
        <Empty>No journal rows — no event ever reached the apply for this load.</Empty>
      )}

      {groups.map((group) => (
        <div key={group.key} className="border-b border-line last:border-b-0">
          <BatchHeader
            guid={group.batchGuid}
            batch={group.batchGuid ? batches.get(group.batchGuid) : undefined}
            first={group.entries[0]!}
          />
          {group.entries.map((entry) => (
            <ChangeRow key={entry.seq} entry={entry} />
          ))}
        </div>
      ))}

      <LoadMore
        hasNext={query.hasNextPage}
        loading={query.isFetchingNextPage}
        onClick={() => void query.fetchNextPage()}
        label="Load older"
      />
    </Panel>
  );
}

interface Group {
  key: string;
  batchGuid: string | null;
  entries: Change[];
}

function groupByBatch(changes: Change[]): Group[] {
  const groups: Group[] = [];
  for (const entry of changes) {
    const last = groups[groups.length - 1];
    if (last && last.batchGuid === entry.batch_guid) {
      last.entries.push(entry);
    } else {
      groups.push({
        key: `${entry.batch_guid ?? "none"}-${entry.seq}`,
        batchGuid: entry.batch_guid,
        entries: [entry],
      });
    }
  }
  return groups;
}

function BatchHeader({
  guid,
  batch,
  first,
}: {
  guid: string | null;
  batch?: Batch;
  first: Change;
}) {
  if (!guid) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-raised px-4 py-2">
        <Badge tone="violet">no batch</Badge>
        <span className="text-xs text-muted">
          Written outside the mobile inbox — a CTMS or system event.
        </span>
      </div>
    );
  }

  const device = batch?.device_guid ?? first.device_guid;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-l-2 border-line-strong bg-raised px-4 py-1.5">
      <span className="label shrink-0">batch</span>
      <Guid value={guid} to={linkTo.batch(guid)} />
      {batch && (
        <span className="shrink-0 whitespace-nowrap text-[11px] text-muted">
          {batch.events.length} sent
        </span>
      )}
      {batch && batch.status !== "applied" && <StatusBadge status={batch.status} />}
      <span className="flex shrink-0 items-center gap-1.5 text-[11px]">
        <span className="label">device</span>
        <Guid value={device} to={linkTo.device(device)} />
      </span>
      {batch && (
        <span className="ml-auto shrink-0">
          <Time value={batch.received_at} />
        </span>
      )}
    </div>
  );
}

function ChangeRow({ entry }: { entry: Change }) {
  const applied = entry.outcome === "applied";
  const delta = useJsonDisclosure(entry.fields_delta, "delta");

  return (
    <div className="row row-hover">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="mono shrink-0 text-xs text-faint" title={SEQ.id}>
          {entry.seq}
        </span>

        {applied ? (
          <span
            className="size-1.5 shrink-0 rounded-full bg-ok"
            title="applied — written to the load and given a cursor"
          />
        ) : (
          <OutcomeBadge outcome={entry.outcome} />
        )}
        <ReasonBadge code={entry.reason_code} outcome={entry.outcome} />
        <EventType value={entry.event_type} />

        {entry.entity_type !== entry.root_type && (
          <span className="shrink-0 text-xs text-body">{entry.entity_type}</span>
        )}
        {entry.operation === "delete" && <Badge tone="bad">delete</Badge>}
        {entry.provenance !== "driver" && <ProvenanceBadge provenance={entry.provenance} />}

        <span className="mono shrink-0 text-xs text-muted" title={SEQ.serverVersion}>
          v{entry.version}
        </span>
        <Num label="cursor" value={entry.conn_seq} hint={SEQ.cursor} />
        <Guid value={entry.event_guid} to={linkTo.event(entry.event_guid)} />
        {delta.toggle}

        <span className="ml-auto shrink-0">
          <Time value={entry.created_at} />
        </span>
      </div>

      {delta.panel}

      {entry.winning_event_guid && (
        <p className="mt-1 flex items-center gap-1.5 text-xs text-warn">
          overridden — the winner was
          <Guid value={entry.winning_event_guid} to={linkTo.event(entry.winning_event_guid)} />
        </p>
      )}
    </div>
  );
}
